import React, { useMemo, useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import PeopleIcon from "@mui/icons-material/People";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Header from "../Includes/Header";
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import { useNavigate } from "react-router-dom";
import { sendBulkHolidayWhatsapp, sendBulkEmail } from "../Apihelper/WhatsappBulkApi.js";
import { AutocompleteMember } from "../Apihelper/MemberApiHelpr.js";
import AppToast from '../../components/Toast/AppToast';

const DATE_DISPLAY_FORMAT = "DD-MM-YYYY";

function formatTemplateDate(value) {
  if (value == null || !dayjs.isDayjs(value) || !value.isValid()) return "";
  return value.format(DATE_DISPLAY_FORMAT);
}

export const HOLIDAY_TEMPLATE_BODY = `Dear Member,
Our gym will remain closed on {{1}} on account of {{2}}.

🏋️ We will reopen on {{3}} as per regular timings.

Thank you for your support!

Thank you for choosing MTTMG! 🙏`;

function buildPreviewBody(closeDate, holidayName, reopenDate) {
  return HOLIDAY_TEMPLATE_BODY.replace("{{1}}", closeDate || "…")
    .replace("{{2}}", holidayName || "…")
    .replace("{{3}}", reopenDate || "…");
}

export default function BulkHolidayWhatsapp() {
  const { colors: appColors } = useAppTheme();
  const fieldSx = buildFieldSx(appColors);
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  // WhatsApp state
  const [closeDate, setCloseDate] = useState(null);
  const [holidayName, setHolidayName] = useState("");
  const [reopenDate, setReopenDate] = useState(null);
  const [sending, setSending] = useState(false);

  // Email state
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]); // [{id, search_data}]
  const [memberOptions, setMemberOptions] = useState([]);
  const [memberSearchLoading, setMemberSearchLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  const handleMemberSearch = useCallback(async (searchText) => {
    if (!searchText || searchText.length < 1) { setMemberOptions([]); return; }
    setMemberSearchLoading(true);
    try {
      const { token, id: userId } = JSON.parse(localStorage.getItem("userdata") || "{}");
      const res = await AutocompleteMember(token, userId, searchText);
      if (res.code === "200" || res.code === 200) {
        setMemberOptions(Array.isArray(res.data) ? res.data : []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setMemberSearchLoading(false);
    }
  }, []);

  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (!localStorage.getItem("userdata")) navigate("/");
  }, [navigate]);

  const closeDateStr = formatTemplateDate(closeDate);
  const reopenDateStr = formatTemplateDate(reopenDate);

  const previewText = useMemo(
    () => buildPreviewBody(closeDateStr, holidayName.trim(), reopenDateStr),
    [closeDateStr, holidayName, reopenDateStr]
  );

  const handleSendWhatsapp = async () => {
    if (!closeDateStr || !holidayName.trim() || !reopenDateStr) {
      setToast({ open: true, message: "Please choose both dates and enter the holiday name before sending.", severity: "warning" });
      return;
    }
    setSending(true);
    try {
      const userData = JSON.parse(localStorage.getItem("userdata") || "{}");
      const res = await sendBulkHolidayWhatsapp(userData.token, userData.id, {
        variable_1: closeDateStr,
        variable_2: holidayName.trim(),
        variable_3: reopenDateStr,
        template_key: "holiday_close_open_info",
      });
      const ok = res.code === 200 || res.code === "200";
      setToast({ open: true, message: ok ? res.message || "Bulk holiday message queued successfully." : res.message || "Request failed.", severity: ok ? "success" : "error" });
    } catch (e) {
      setToast({ open: true, message: e.message || "Network error.", severity: "error" });
    } finally {
      setSending(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      setToast({ open: true, message: "Please enter a subject and message before sending.", severity: "warning" });
      return;
    }
    setEmailSending(true);
    try {
      const userData = JSON.parse(localStorage.getItem("userdata") || "{}");
      const memberIds = selectedMembers.length > 0 ? selectedMembers.map((m) => m.id) : undefined;

      const res = await sendBulkEmail(userData.token, userData.id, {
        subject: emailSubject.trim(),
        message: emailMessage.trim(),
        ...(memberIds ? { member_ids: memberIds } : {}),
      });
      const ok = res.code === 200 || res.code === "200";
      setToast({
        open: true,
        message: ok
          ? `${res.message || "Bulk email queued successfully."}${res.eligible_count != null ? ` (${res.eligible_count} recipients)` : ""}`
          : res.message || "Request failed.",
        severity: ok ? "success" : "error",
      });
      if (ok) {
        setEmailSubject("");
        setEmailMessage("");
        setSelectedMembers([]);
        setMemberOptions([]);
      }
    } catch (e) {
      setToast({ open: true, message: e.message || "Network error.", severity: "error" });
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#EFEDF1" }}>
      <CssBaseline />
      <Box><Header /></Box>
      <Box
        component="main"
        sx={{ flexGrow: 1, minWidth: 0, overflowY: "auto", pt: { xs: 8, sm: 9 }, px: { xs: 2, sm: 3 }, pb: 4 }}
      >
        <Typography variant="h5" sx={{ color: appColors.evergreen, fontWeight: 700, mb: 0.5 }}>
          Bulk Messaging
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
          Send bulk WhatsApp messages or emails to all active members or a specific group.
        </Typography>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            mb: 3,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 44 },
            "& .Mui-selected": { color: appColors.evergreen },
            "& .MuiTabs-indicator": { backgroundColor: appColors.evergreen },
          }}
        >
          <Tab icon={<WhatsAppIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Holiday WhatsApp" />
          <Tab icon={<EmailIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Bulk Email" />
        </Tabs>

        {/* ── WhatsApp Tab ── */}
        {tab === 0 && (
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} md={6} sx={{ display: "flex" }}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2, boxShadow: "0 2px 12px rgba(0,0,0,0.08)", width: "100%", flex: 1, display: "flex", flexDirection: "column" }}>
                <Typography variant="subtitle1" fontWeight={700} color={appColors.evergreen} gutterBottom>
                  Template variables
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  {"{{1}}"} = closed on · {"{{2}}"} = holiday · {"{{3}}"} = reopen on. Dates are sent as{" "}
                  <strong>{DATE_DISPLAY_FORMAT}</strong> (day-month-year).
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <StackedFields
                    closeDate={closeDate} setCloseDate={setCloseDate}
                    holidayName={holidayName} setHolidayName={setHolidayName}
                    reopenDate={reopenDate} setReopenDate={setReopenDate}
                  />
                </LocalizationProvider>
                <Button
                  variant="contained"
                  size="large"
                  disabled={sending}
                  onClick={handleSendWhatsapp}
                  startIcon={<WhatsAppIcon />}
                  sx={{ mt: "auto", pt: 2, alignSelf: "flex-start", textTransform: "none", fontWeight: 700, bgcolor: "#128C7E", "&:hover": { bgcolor: "#0d6b60" }, minWidth: 200 }}
                >
                  {sending ? <CircularProgress size={22} color="inherit" /> : "Send bulk WhatsApp"}
                </Button>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: "flex" }}>
              <Box sx={{ width: "100%", flex: 1, display: "flex", flexDirection: "column" }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Preview</Typography>
                <PhonePreview text={previewText} sx={{ flex: 1 }} />
              </Box>
            </Grid>
          </Grid>
        )}

        {/* ── Email Tab ── */}
        {tab === 1 && (
          <Box sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", lg: "row" }, alignItems: "flex-start" }}>
            {/* Compose */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, boxShadow: "0 2px 12px rgba(0,0,0,0.08)", flex: { xs: "1 1 auto", lg: "0 0 60%" }, width: { xs: "100%", lg: "auto" } }}>
              <Typography variant="subtitle1" fontWeight={700} color={appColors.evergreen} gutterBottom>
                Compose email
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2.5 }}>
                Leave <strong>Select Members</strong> empty to send to <strong>all active members</strong> who have an email address.
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <TextField
                  label="Subject"
                  placeholder="e.g. Special Offer for Members!"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  fullWidth
                  size="small"
                  sx={fieldSx}
                />
                <TextField
                  label="Message"
                  placeholder="Dear member, we have an exciting offer for you..."
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  fullWidth
                  multiline
                  rows={6}
                  sx={fieldSx}
                />
                <Autocomplete
                  multiple
                  options={memberOptions}
                  value={selectedMembers}
                  loading={memberSearchLoading}
                  getOptionLabel={(option) => option.search_data || ""}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  onChange={(_, newValue) => setSelectedMembers(newValue)}
                  onInputChange={(_, inputVal) => handleMemberSearch(inputVal)}
                  filterOptions={(x) => x}
                  noOptionsText="Type to search members…"
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option.id}
                        label={option.search_data.split('|')[0].trim()}
                        size="small"
                        sx={{ bgcolor: appColors.leafSoft, color: appColors.evergreen, fontWeight: 500, maxWidth: 160, mb: 0.5 }}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Members (optional)"
                      placeholder={selectedMembers.length === 0 ? "Search by name, phone… leave empty for ALL" : ""}
                      helperText="Leave empty to send to all active members with an email address."
                      size="small"
                      sx={{
                        ...fieldSx,
                        "& .MuiInputBase-root": { flexWrap: "wrap", alignItems: "flex-start", minHeight: 42 },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {memberSearchLoading ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  componentsProps={{
                    paper: { sx: { backgroundColor: "#ffffff", boxShadow: "0 4px 20px rgba(0,0,0,0.12)" } },
                  }}
                />
              </Box>

              <Button
                variant="contained"
                size="large"
                disabled={emailSending}
                onClick={handleSendEmail}
                startIcon={<SendIcon />}
                sx={{ mt: 3, textTransform: "none", fontWeight: 700, bgcolor: appColors.evergreen, "&:hover": { bgcolor: appColors.pine }, minWidth: 200 }}
              >
                {emailSending ? <CircularProgress size={22} color="inherit" /> : "Send bulk email"}
              </Button>
            </Paper>

            {/* Preview — sticky on large screens */}
            <Paper
              elevation={0}
              sx={{
                p: 3, borderRadius: 2, boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                flex: { xs: "1 1 auto", lg: "0 0 38%" },
                width: { xs: "100%", lg: "auto" },
                position: { lg: "sticky" },
                top: { lg: 80 },
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} color={appColors.evergreen} gutterBottom>
                Email preview
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary">Subject</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: emailSubject ? "#111" : "#aaa" }}>
                  {emailSubject || "Your subject will appear here…"}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Message body</Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 0.5, whiteSpace: "pre-wrap", wordBreak: "break-word", color: emailMessage ? "#111" : "#aaa", minHeight: 120 }}
                >
                  {emailMessage || "Your message body will appear here…"}
                </Typography>
              </Box>
              <Divider sx={{ mt: 2, mb: 1.5 }} />
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                <PeopleIcon sx={{ fontSize: 16, color: "text.secondary", mt: 0.2 }} />
                <Typography variant="caption" color="text.secondary">
                  <strong>Recipients: </strong>
                  {selectedMembers.length === 0
                    ? "All active members with an email"
                    : `${selectedMembers.length} selected member${selectedMembers.length > 1 ? "s" : ""}`}
                </Typography>
              </Box>
              {selectedMembers.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                  {selectedMembers.map((m) => (
                    <Chip key={m.id} label={m.search_data.split('|')[0].trim()} size="small" sx={{ bgcolor: appColors.leafSoft, color: appColors.evergreen, fontSize: "0.7rem" }} />
                  ))}
                </Box>
              )}
            </Paper>
          </Box>
        )}
      </Box>

      <AppToast open={toast.open} message={toast.message} severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))} />
    </Box>
  );
}

const buildFieldSx = (appColors) => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: appColors.evergreen },
    "&.Mui-focused fieldset": { borderColor: appColors.evergreen },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: appColors.evergreen },
});

const buildDatePickerSlotProps = (appColors) => ({
  textField: {
    size: "small",
    fullWidth: true,
    sx: {
      "& .MuiOutlinedInput-root": {
        bgcolor: "#fff",
        "& fieldset": { borderColor: "#e2e8f0" },
        "&:hover fieldset": { borderColor: appColors.evergreen },
        "&.Mui-focused fieldset": { borderColor: appColors.evergreen },
      },
    },
  },
});

function StackedFields({ closeDate, setCloseDate, holidayName, setHolidayName, reopenDate, setReopenDate }) {
  const { colors: appColors } = useAppTheme();
  const datePickerSlotProps = buildDatePickerSlotProps(appColors);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <DatePicker label={'Closed on ({{1}})'} value={closeDate} onChange={setCloseDate} format={DATE_DISPLAY_FORMAT} slotProps={datePickerSlotProps} />
      <TextField label={'Holiday / reason ({{2}})'} placeholder="e.g. Holi" value={holidayName} onChange={(e) => setHolidayName(e.target.value)} fullWidth size="small" />
      <DatePicker
        label={'Reopens on ({{3}})'}
        value={reopenDate}
        onChange={setReopenDate}
        format={DATE_DISPLAY_FORMAT}
        slotProps={datePickerSlotProps}
        minDate={closeDate != null && typeof closeDate.isValid === "function" && closeDate.isValid() ? closeDate : undefined}
      />
    </Box>
  );
}

function PhonePreview({ text, sx: sxProp = {} }) {
  return (
    <Paper
      elevation={0}
      sx={{
        maxWidth: 360, width: "100%", mx: { xs: "auto", md: 0 }, borderRadius: 3, overflow: "hidden",
        border: "1px solid", borderColor: "divider", bgcolor: "#e5ddd5",
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c9b79c' fill-opacity='0.35'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        p: 2, minHeight: 280, height: "100%", display: "flex", flexDirection: "column", ...sxProp,
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <Box sx={{ bgcolor: "#dcf8c6", color: "#111", borderRadius: "8px 8px 0 8px", p: 1.5, ml: 2, boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)", fontSize: "0.875rem", lineHeight: 1.45, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {text}
        </Box>
        <Typography variant="caption" display="block" textAlign="right" sx={{ mt: 0.5, pr: 1, color: "rgba(0,0,0,0.45)" }}>
          {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </Typography>
      </Box>
      <Divider sx={{ my: 1, borderColor: "rgba(0,0,0,0.06)" }} />
      <Typography variant="caption" color="text.secondary" sx={{ px: 1, display: "block", pb: 1, flexShrink: 0 }}>
        CTA buttons (phone / review) come from your WhatsApp template in Meta — not edited here.
      </Typography>
    </Paper>
  );
}
